import React, { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BookOpen,
  EyeOff,
  FileText,
  FolderOpen,
  GripVertical,
  Pin,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { HelpCenter, HelpCenterStructureNode, HomepagePinRef } from '../../../types';
import { updateHelpCenterSettings } from '../../../services/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  countCategoryItems,
  flattenStructureNodes,
  getNodePinRef,
  isPinned,
  isStructureNodePublished,
  pinRefKey,
  resolveHomepagePins,
} from '../../../libs/homepage-pins-utils';

interface HomepagePinsSectionProps {
  helpCenter: HelpCenter;
  structure: HelpCenterStructureNode[];
  onHelpCenterUpdate: (helpCenter: HelpCenter) => void;
}

function getNodeTitle(node: HelpCenterStructureNode): string {
  if (node.type === 'category') return node.category?.title || 'Untitled category';
  if (node.item?.item_type === 'article') {
    return node.item.article?.title || 'Untitled article';
  }
  return node.item?.guide?.title || 'Untitled guide';
}

function getTypeLabel(node: HelpCenterStructureNode, t: TFunction): string {
  if (node.type === 'category') return t('helpCenter.highlights.typeCategory');
  if (node.item?.item_type === 'guide') return t('helpCenter.highlights.typeGuide');
  return t('helpCenter.highlights.typeArticle');
}

function formatCategoryCounts(
  counts: { articles: number; guides: number },
  t: TFunction
): string {
  if (counts.articles > 0 && counts.guides > 0) {
    return `${counts.articles} ${counts.articles === 1 ? t('helpCenter.highlights.article') : t('helpCenter.highlights.articles')} · ${counts.guides} ${counts.guides === 1 ? t('helpCenter.highlights.guide') : t('helpCenter.highlights.guides')}`;
  }
  if (counts.articles > 0) {
    return `${counts.articles} ${counts.articles === 1 ? t('helpCenter.highlights.article') : t('helpCenter.highlights.articles')}`;
  }
  if (counts.guides > 0) {
    return `${counts.guides} ${counts.guides === 1 ? t('helpCenter.highlights.guide') : t('helpCenter.highlights.guides')}`;
  }
  return t('helpCenter.highlights.noItemsYet');
}

interface SortablePinCardProps {
  pin: HomepagePinRef;
  node: HelpCenterStructureNode | null;
  onRemove: () => void;
  isSaving: boolean;
}

function SortablePinCard({ pin, node, onRemove, isSaving }: SortablePinCardProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pinRefKey(pin) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  if (!node) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="relative flex flex-col rounded-xl border border-dashed border-destructive/30 bg-destructive/5 p-3"
      >
        <div className="flex items-center gap-1.5">
          <button
            {...attributes}
            {...listeners}
            className="shrink-0 cursor-grab rounded p-0.5 text-muted-foreground hover:bg-accent active:cursor-grabbing"
            type="button"
            aria-label="Drag to reorder"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <p className="min-w-0 flex-1 text-sm font-medium text-destructive">
            {t('helpCenter.highlights.contentNotFound')}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 shrink-0 p-0 text-destructive"
            onClick={onRemove}
            disabled={isSaving}
            type="button"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="mt-1 pl-5 text-xs text-muted-foreground">
          {t('helpCenter.highlights.contentNotFoundDescription')}
        </p>
      </div>
    );
  }

  const title = getNodeTitle(node);
  const typeLabel = getTypeLabel(node, t);
  const description =
    node.type === 'category'
      ? node.category?.description
      : node.item?.article?.description || node.item?.guide?.description;
  const unpublished = !isStructureNodePublished(node);
  const counts = node.type === 'category' ? countCategoryItems(node) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative flex flex-col overflow-hidden rounded-xl border bg-card p-3 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="absolute inset-0 bg-linear-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

      <div className="relative mb-1.5 inline-flex w-fit items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {node.type === 'category' ? (
          <FolderOpen className="h-2.5 w-2.5" />
        ) : node.item?.item_type === 'guide' ? (
          <BookOpen className="h-2.5 w-2.5" />
        ) : (
          <FileText className="h-2.5 w-2.5" />
        )}
        {typeLabel}
      </div>

      <div className="relative flex items-center gap-1.5">
        <button
          {...attributes}
          {...listeners}
          className="shrink-0 cursor-grab rounded p-0.5 text-muted-foreground hover:bg-accent active:cursor-grabbing"
          type="button"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold leading-snug">
          {title}
        </h3>

        {unpublished && (
          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            <EyeOff className="h-2.5 w-2.5" />
            {t('helpCenter.highlights.hidden')}
          </span>
        )}

        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          disabled={isSaving}
          type="button"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {description && (
        <p className="relative mt-1 line-clamp-2 pl-5 text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      )}

      {counts && (
        <p className="relative mt-1.5 pl-5 text-xs text-muted-foreground">
          {formatCategoryCounts(counts, t)}
        </p>
      )}
    </div>
  );
}

export const HomepagePinsSection: React.FC<HomepagePinsSectionProps> = ({
  helpCenter,
  structure,
  onHelpCenterUpdate,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const pins = helpCenter.settings?.homepage?.pins || [];
  const resolvedPins = useMemo(
    () => resolveHomepagePins(pins, structure),
    [pins, structure]
  );

  const availableNodes = useMemo(
    () => flattenStructureNodes(structure),
    [structure]
  );

  const filteredNodes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return availableNodes;
    return availableNodes.filter(
      ({ node, breadcrumb }) =>
        breadcrumb.toLowerCase().includes(query) ||
        getTypeLabel(node, t).toLowerCase().includes(query)
    );
  }, [availableNodes, searchQuery, t]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const savePins = async (nextPins: HomepagePinRef[]) => {
    setIsSaving(true);
    try {
      const response = await updateHelpCenterSettings(
        String(helpCenter.id),
        { homepage: { pins: nextPins } },
        undefined,
        false
      );

      if (response.ok && response.helpCenter) {
        onHelpCenterUpdate(response.helpCenter);
      } else {
        throw new Error(response.error || t('helpCenter.highlights.updateFailed'));
      }
    } catch (error) {
      console.error('Error saving highlights:', error);
      toast.error(t('helpCenter.highlights.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = pins.findIndex((pin) => pinRefKey(pin) === active.id);
    const newIndex = pins.findIndex((pin) => pinRefKey(pin) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const nextPins = [...pins];
    const [moved] = nextPins.splice(oldIndex, 1);
    nextPins.splice(newIndex, 0, moved);
    void savePins(nextPins);
  };

  const handleAddPin = (node: HelpCenterStructureNode) => {
    const ref = getNodePinRef(node);
    if (!ref || isPinned(pins, ref)) return;
    void savePins([...pins, ref]);
    toast.success(t('helpCenter.highlights.added'));
  };

  const handleRemovePin = (pin: HomepagePinRef) => {
    void savePins(pins.filter((item) => pinRefKey(item) !== pinRefKey(pin)));
  };

  return (
    <div className="mb-6 rounded-2xl border bg-linear-to-br from-primary/5 via-card to-card p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('helpCenter.highlights.title')}</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {t('helpCenter.highlights.description')}
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => setShowPicker(true)}
          disabled={isSaving || structure.length === 0}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('helpCenter.highlights.add')}
        </Button>
      </div>

      {resolvedPins.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-background/60 px-6 py-10 text-center">
          <Pin className="mx-auto mb-3 h-8 w-8 text-muted-foreground/60" />
          <p className="text-sm font-medium">{t('helpCenter.highlights.emptyTitle')}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('helpCenter.highlights.emptyDescription')}
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pins.map((pin) => pinRefKey(pin))}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {resolvedPins.map(({ pin, node }) => (
                <SortablePinCard
                  key={pinRefKey(pin)}
                  pin={pin}
                  node={node}
                  onRemove={() => handleRemovePin(pin)}
                  isSaving={isSaving}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('helpCenter.highlights.dialogTitle')}</DialogTitle>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('helpCenter.highlights.searchPlaceholder')}
              className="pl-9"
            />
          </div>

          <div className="max-h-80 space-y-1 overflow-y-auto pr-1">
            {filteredNodes.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t('helpCenter.highlights.noResults')}
              </p>
            ) : (
              filteredNodes.map(({ node, breadcrumb }) => {
                const ref = getNodePinRef(node);
                const alreadyPinned = ref ? isPinned(pins, ref) : false;
                const unpublished = !isStructureNodePublished(node);

                return (
                  <button
                    key={breadcrumb + getTypeLabel(node, t)}
                    type="button"
                    disabled={alreadyPinned || isSaving}
                    onClick={() => {
                      handleAddPin(node);
                      setShowPicker(false);
                      setSearchQuery('');
                    }}
                    className="flex w-full items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-background">
                      {node.type === 'category' ? (
                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                      ) : node.item?.item_type === 'guide' ? (
                        <BookOpen className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{breadcrumb}</p>
                      <p className="text-xs text-muted-foreground">
                        {getTypeLabel(node, t)}
                        {unpublished ? ` · ${t('helpCenter.highlights.unpublished')}` : ''}
                        {alreadyPinned ? ` · ${t('helpCenter.highlights.alreadyHighlighted')}` : ''}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
